import { Button } from "@/components/core/buttons/button";
import { WebhookEndpointForm } from "@/components/webhooks/webhook-endpoint-form";
import { useWebhookEndpointUpdateMutation } from "@/features/dashboard/api/queries/webhook-endpoint-query";
import type { WebhookEndpoint } from "@/features/dashboard/types/webhook-endpoint";
import * as Dialog from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "sonner";

interface EditWebhookEndpointModalProps {
	endpoint: WebhookEndpoint;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export function EditWebhookEndpointModal({ endpoint, isOpen, onClose, onSuccess }: EditWebhookEndpointModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useWebhookEndpointUpdateMutation(endpoint.id?.toString() ?? "");

	const handleSubmit = async (values: WebhookEndpoint) => {
		setIsSubmitting(true);
		try {
			await updateMutation.mutateAsync(values);
			toast.success("Webhook endpoint updated successfully");
			onSuccess?.();
			onClose();
		} catch {
			toast.error("Failed to update webhook endpoint");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Webhook Endpoint</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<WebhookEndpointForm endpoint={endpoint} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="webhook-endpoint-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#webhook-endpoint-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Updating..." : "Update Endpoint"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
